import styled from "@emotion/styled";
import type { FC } from "react";

import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import type { LocalAudioRecordingTranscriptTurnRecord } from "@/domains/dexie-db/models";
import { resolveTranscriptSpeakerDisplayLabel } from "@/modules/audio-recording/transcript-speaker-identity";

type Props = {
  transcriptTurns: LocalAudioRecordingTranscriptTurnRecord[];
  emptyMessage?: string;
};

export const AudioRecordingTranscriptionList: FC<Props> = ({ transcriptTurns, emptyMessage }) => {
  if (!transcriptTurns.length) {
    return (
      <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
        {emptyMessage ?? "Transcript will appear here once recording starts."}
      </MDSText>
    );
  }

  return (
    <Container>
      {transcriptTurns.length ? (
        <TranscriptsList>
          {transcriptTurns.map(turn => (
            <TranscriptItem key={turn.id}>
              <ChannelPill>
                {`[${resolveTranscriptSpeakerDisplayLabel({
                  speakerVariant: turn.content.speakerVariant,
                  channel: turn.channel,
                })}]`}
              </ChannelPill>
              <TranscriptText variant={MDSTextVariant.TextXs}>
                {(turn.content?.words ?? []).map(word => word.text).join(" ")}
              </TranscriptText>
            </TranscriptItem>
          ))}
        </TranscriptsList>
      ) : (
        <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
          {emptyMessage ?? "Transcript will appear here once recording starts."}
        </MDSText>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TranscriptsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TranscriptItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const ChannelPill = styled.span`
  border-radius: 999px;
  font-size: 0.75rem;
  min-width: 2.5rem;
`;

const TranscriptText = styled(MDSText)`
  flex: 1;
  margin: 0;
`;
